'use client'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { LogInIcon } from 'lucide-react'
import { AuthError } from 'next-auth'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { SIGN_IN_PAGE } from '@/constants/pages'
import { loadingOverlayAtom } from '@/components/providers/atoms-provider'
import { LogoAmsSVG } from '@/components/logo'
import { Fader } from '@/components/fader'
import { Button } from '@/components/button'
import type { SlideShowItem } from '@/api/application-crud'
import { Card, CardContent } from '@/components/card'

type SignInClientPageProps = {
    providers: { id: string; name: string }[]
    errorMessage?: string
    backgroundSlides?: SlideShowItem[]
}

function SignInClientPage(props: SignInClientPageProps): JSX.Element {
    const { providers, errorMessage, backgroundSlides = [] } = props
    const setOverlay = useSetAtom(loadingOverlayAtom)
    const router = useRouter()
    const backgroundUrls = (() => {
        const slides: string[] = []
        for (const slide of backgroundSlides) {
            if (slide.url) {
                slides.push(slide.url)
            }
        }
        return slides
    })()

    useEffect(() => {
        return () => {
            setOverlay({ visible: false })
        }
    }, [setOverlay, backgroundSlides])

    return (
        <Fader className={clsx('flex h-full w-full items-center justify-center')} urls={backgroundUrls}>
            <div className="flex h-full flex-col justify-around">
                <Card className="bg-white dark:bg-neutral-800">
                    <CardContent size="zero" className="flex flex-col gap-16 px-8 py-8">
                        <div className="flex flex-col gap-8">
                            <div className="flex items-center justify-center gap-1">
                                <LogoAmsSVG/>
                            </div>
                        </div>
                        {errorMessage ? (
                            <div className="text-center text-sm font-medium text-danger-600 dark:text-danger-500">
                                {errorMessage}
                            </div>
                        ) : null}
                        {providers.map((provider) => (
                            <Button
                                key={provider.id}
                                className="w-full sm:w-auto"
                                onClick={() => {
                                    try {
                                        setOverlay({ message: 'Autenticazione in corso...', visible: true })
                                        signIn(provider.id, { callbackUrl: '/' })
                                    } catch (catchedError) {
                                        if (catchedError instanceof AuthError) {
                                            router.push(`${SIGN_IN_PAGE}?error=${catchedError.type}`, { scroll: false })
                                        }
                                        throw catchedError
                                    }
                                }}
                            >
                                <LogInIcon className="size-4" />
                                Accedi con {provider.name}
                            </Button>
                        ))}
                    </CardContent>
                </Card>
            </div>
        </Fader>
    )
}

export default SignInClientPage
